/**
 * Mordeczka.eu Admin - Users Page
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
  Tag,
  Switch,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { usersApi, rolesApi } from "../services/api";
import type { User, UserCreate, UserUpdate } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

const roleColors: Record<string, string> = {
  superadmin: "red",
  admin: "blue",
  user: "green",
};

const getRoleColor = (roleName: string) => {
  return roleColors[roleName.toLowerCase()] || "default";
};

export function UsersPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<User | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const { canEdit, canDelete } = usePermissions();

  // Fetch users
  const { data, isLoading } = useQuery({
    queryKey: ["users", pagination],
    queryFn: () =>
      usersApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  // Fetch roles for select
  const { data: roles } = useQuery({
    queryKey: ["roles-all"],
    queryFn: () => rolesApi.list({ page_size: 100 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: UserCreate) => usersApi.create(data),
    onSuccess: () => {
      message.success("Użytkownik utworzony pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć użytkownika"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      message.success("Użytkownik zaktualizowany pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować użytkownika"),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      message.success("Użytkownik usunięty pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: () => message.error("Nie udało się usunąć użytkownika"),
  });

  const openModal = (item?: User) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      email: item?.email || "",
      display_name: item?.display_name || "",
      role_id: item?.role_id || item?.role?.id || null,
      is_active: item?.is_active ?? true,
      password: "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Remove empty password for update
      if (editingItem && !values.password) {
        delete values.password;
      }

      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Nazwa wyświetlana",
      dataIndex: "display_name",
      key: "display_name",
      render: (name) => name || "—",
    },
    {
      title: "Rola",
      key: "role",
      width: 150,
      render: (_, record) => {
        const roleName = record.role?.name || "Brak roli";
        return <Tag color={getRoleColor(roleName)}>{roleName}</Tag>;
      },
      sorter: (a, b) => (a.role?.name || "").localeCompare(b.role?.name || ""),
    },
    {
      title: "Aktywny",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (isActive: boolean) =>
        isActive ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Tak
          </Tag>
        ) : (
          <Tag icon={<StopOutlined />} color="error">
            Nie
          </Tag>
        ),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: User) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tego użytkownika?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            okButtonProps={{ danger: true }}
            cancelText="Anuluj"
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Użytkownicy
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nowego
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `Łącznie ${total} użytkowników`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj użytkownika" : "Dodaj użytkownika"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Proszę wprowadzić email" },
              { type: "email", message: "Proszę wprowadzić poprawny email" },
            ]}
          >
            <Input placeholder="email@example.com" />
          </Form.Item>

          <Form.Item name="display_name" label="Nazwa wyświetlana">
            <Input placeholder="np. Jan Kowalski" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingItem ? "Nowe hasło (opcjonalnie)" : "Hasło"}
            rules={
              editingItem
                ? []
                : [
                    { required: true, message: "Proszę wprowadzić hasło" },
                    { min: 8, message: "Hasło musi mieć minimum 8 znaków" },
                    {
                      max: 72,
                      message: "Hasło może mieć maksymalnie 72 znaki",
                    },
                  ]
            }
          >
            <Input.Password placeholder="Minimum 8 znaków" />
          </Form.Item>

          <Form.Item name="role_id" label="Rola">
            <Select allowClear placeholder="Wybierz rolę user">
              {roles?.items.map((role: any) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {editingItem && (
            <Form.Item name="is_active" label="Aktywny" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default UsersPage;
